/*
  NDIS 0138 Readiness readiness logic.
  Phase 4: 22-field intake + 9-field classifier output for the SIL Registration
  Group 0138 / 1 July 2026 transition pathway.
*/

import { SECTIONS, EVIDENCE_ITEMS, READY_DOMAINS, FIELD_VERIFICATION_LABELS, getCanonicalEvidenceState } from "@/lib/questionnaire";
import { buildSourceVerification, determineSourceVerificationStatus } from "@/lib/sourceVerification";
import { buildLegalReferralRows } from "@/lib/legalReferral";

// ---------------------------------------------------------------------------
// Sensitive / boundary scanning
// ---------------------------------------------------------------------------

const SENSITIVE_PATTERNS = [
  /\b(mr|mrs|ms|miss|dr)\.?\s+[A-Z][a-z]+/i,
  /\b\d+\s+[A-Z][a-z]+\s+(street|st|road|rd|avenue|ave|drive|dr|crescent|cres|lane|ln|court|ct|place|pl)\b/i,
  /\bndis\s*#?\s*\d{4,}\b/i,
  /\b(dob|date of birth)\b/i,
  /\bmedicare\s*#?\s*\d/i,
  /\b(diagnos(ed|is)|prognosis|condition of)\b/i
];
const LEGAL_PATTERNS = [/legal\s+advice/i, /lawyer/i, /solicitor/i];
const AUDIT_PATTERNS = [/audit\s+(certificate|certification)/i, /certify\s+(us|me)/i];
const REG_APPROVAL_PATTERNS = [/registration\s+approval/i, /approve\s+(our|my)\s+registration/i, /get\s+(us|me)\s+registered/i];
const GUARANTEE_PATTERNS = [/compliance\s+guarantee/i, /guarantee[a-z]*\s+(compliance|outcome|registration)/i, /guarantee\b/i];

function scanFreeText(text) {
  if (!text || typeof text !== "string") return { reasons: [] };
  const reasons = [];
  if (SENSITIVE_PATTERNS.some((re) => re.test(text)))
    reasons.push("Sensitive identifying information appears to have been entered in the free-text field.");
  if (LEGAL_PATTERNS.some((re) => re.test(text)))
    reasons.push("The free-text field appears to request legal advice. This service does not provide legal advice.");
  if (AUDIT_PATTERNS.some((re) => re.test(text)))
    reasons.push("The free-text field appears to request audit certification. This service does not provide audit certification.");
  if (REG_APPROVAL_PATTERNS.some((re) => re.test(text)))
    reasons.push("The free-text field appears to request registration approval. This service cannot approve registration.");
  if (GUARANTEE_PATTERNS.some((re) => re.test(text)))
    reasons.push("The free-text field appears to request a compliance guarantee. No such guarantee can be provided.");
  return { reasons };
}

// ---------------------------------------------------------------------------
// Provider categories (Phase 2 — kept for backwards compatibility)
// ---------------------------------------------------------------------------

export const PROVIDER_CATEGORIES = {
  REGISTERED_UPLIFT: {
    id: "REGISTERED_UPLIFT",
    title: "Registered provider needing 0138 readiness uplift",
    summary: "Your responses indicate a current registration with evidence or operational gaps that warrant a structured readiness uplift before next audit."
  },
  UNREGISTERED_SIL_PATHWAY: {
    id: "UNREGISTERED_SIL_PATHWAY",
    title: "Unregistered SIL provider needing registration pathway review",
    summary: "Your responses indicate SIL delivery without confirmed registration coverage. A pathway review is the prudent first step."
  },
  SIL_EXIT_TRANSITION: {
    id: "SIL_EXIT_TRANSITION",
    title: "SIL provider needing exit or transition planning",
    summary: "Your responses indicate an intention to wind-down or transition. Continuity planning and a controlled handover should take priority."
  },
  MIXED_MANUAL_REVIEW: {
    id: "MIXED_MANUAL_REVIEW",
    title: "Mixed 0115 / 0138 exposure requiring manual review",
    summary: "Your responses indicate both 0115 and 0138 service exposure. Categorisation requires manual review by a qualified consultant."
  },
  UNKNOWN_MANUAL_REVIEW: {
    id: "UNKNOWN_MANUAL_REVIEW",
    title: "Unknown \u00b7 manual review required",
    summary: "There is not enough signal to categorise reliably, or the responses contain conflicting risk indicators. Manual review is required."
  }
};

// ---------------------------------------------------------------------------
// Evidence model
// ---------------------------------------------------------------------------

const EVIDENCE_OUTCOMES = {
  current: { rag: "green", label: "Current & available", short: "Current", weight: 0 },
  review: { rag: "amber", label: "Exists \u2014 needs review", short: "Needs review", weight: 2 },
  missing: { rag: "red", label: "Missing", short: "Missing", weight: 3 },
  unsure: { rag: "red", label: "Needs Clarification", short: "Clarify", weight: 3 },
  na: { rag: "na", label: "Not applicable", short: "N/A", weight: 0 },
  unanswered: { rag: "unknown", label: "Not yet answered", short: "Unanswered", weight: 1 }
};

const RISK_LEVELS = {
  risk_staffing_supervision: {
    strong: { level: "ok", label: "Documented supervision in place" },
    stable_informal: { level: "low", label: "Supervision informal \u2014 recommend documentation" },
    pressure_recent: { level: "medium", label: "Recent staffing pressure noted" },
    pressure_significant: { level: "high", label: "Significant staffing pressure \u2014 escalation indicated" },
    unsure: { level: "medium", label: "Position is unclear \u2014 clarify before audit" }
  },
  risk_continuity_concerns: {
    none: { level: "ok", label: "No continuity concerns reported" },
    minor_monitored: { level: "low", label: "Minor concerns, monitored" },
    active: { level: "medium", label: "Active concerns under management" },
    significant: { level: "high", label: "Significant continuity risk \u2014 manual review indicated" },
    unsure: { level: "medium", label: "Continuity position unclear" }
  },
  risk_practice_gap: {
    close: { level: "ok", label: "Practice closely matches documents" },
    minor: { level: "low", label: "Minor practice variation" },
    notable: { level: "medium", label: "Notable variation from documents" },
    significant: { level: "high", label: "Significant documentation\u2013practice gap" },
    unsure: { level: "medium", label: "Alignment between policy and practice unclear" }
  }
};

const RISK_QUESTION_LABELS = {
  risk_staffing_supervision: "Staffing & supervision",
  risk_continuity_concerns: "Service continuity",
  risk_practice_gap: "Policy\u2013practice alignment"
};

function collectEvidenceItems(answers) {
  return EVIDENCE_ITEMS.map((item) => {
    const ans = answers[item.id];
    const meta = ans ? EVIDENCE_OUTCOMES[ans] || EVIDENCE_OUTCOMES.unanswered : EVIDENCE_OUTCOMES.unanswered;
    // Design Specification 001 — Applicability Assessment (Phase 1).
    const applicability =
      ans === "na"
        ? "not_applicable"
        : ans === "unsure"
          ? "needs_clarification"
          : ans
            ? "applicable"
            : "unanswered";
    // P2 — per-field source verification (display/transparency only; never
    // feeds scoring, classification, or report-level source verification).
    const fieldVerification =
      ans === "na"
        ? "not_applicable"
        : ans === "unsure"
          ? "requires_clarification"
          : ans
            ? answers[`${item.id}__verification`] || "provider_reported"
            : null;
    return {
      id: item.id,
      number: item.number,
      title: item.title,
      helper: item.helper,
      domain: item.domain,
      answer: ans || null,
      applicability,
      notApplicableReason:
        ans === "na" ? String(answers[`${item.id}__na_reason`] || "").trim() : "",
      evidenceNote: String(answers[`${item.id}__evidence_note`] || "").trim(),
      fieldVerification,
      fieldVerificationLabel: fieldVerification ? FIELD_VERIFICATION_LABELS[fieldVerification] : "",
      // Phase 8C — canonical evidence state (display-only, derived on read).
      canonicalState: getCanonicalEvidenceState(
        ans,
        answers[`${item.id}__verification`],
        String(answers[`${item.id}__na_reason`] || "").trim()
      ),
      rag: meta.rag,
      label: meta.label,
      shortLabel: meta.short,
      weight: meta.weight
    };
  });
}

function rollupDomain(items) {
  const counts = { green: 0, amber: 0, red: 0, na: 0, unknown: 0 };
  items.forEach((i) => {
    counts[i.rag] = (counts[i.rag] || 0) + 1;
  });
  const considered = counts.green + counts.amber + counts.red;
  let rag = "unknown";
  if (counts.red > 0) rag = "red";
  else if (counts.amber > 0) rag = "amber";
  else if (counts.green > 0) rag = "green";
  return { counts, considered, rag };
}

function collectDomainRollup(evidenceItems) {
  return READY_DOMAINS.map((domain) => {
    const items = evidenceItems.filter((i) => i.domain === domain.key);
    const roll = rollupDomain(items);
    return { key: domain.key, title: domain.title, items, ...roll };
  });
}

function collectOperationalRisks(answers) {
  const riskSection = SECTIONS.find((s) => s.id === "risk");
  return riskSection.questions
    // QC-05: practice_supported_decision_making is a readiness/evidence
    // signal only and must never contribute to operational-risk scoring
    // even if a future edit accidentally moves it into the risk section.
    .filter((q) => q.id !== "practice_supported_decision_making")
    .map((q) => {
      const ans = answers[q.id];
      const meta = (RISK_LEVELS[q.id] && RISK_LEVELS[q.id][ans]) || { level: "unknown", label: "Not answered" };
      return {
        id: q.id,
        topic: RISK_QUESTION_LABELS[q.id] || q.id,
        prompt: q.prompt,
        answer: ans,
        level: meta.level,
        label: meta.label
      };
    });
}

// ---------------------------------------------------------------------------
// Manual review detection
// ---------------------------------------------------------------------------

const SAFEGUARDING_TRIGGER_REASONS = {
  safeguarding_abuse_neglect: "Possible abuse or neglect concern reported.",
  safeguarding_medication_safety: "Possible unsafe medication support reported.",
  safeguarding_restrictive_practice: "Restrictive practice concern reported.",
  safeguarding_serious_incident: "Possible under\u2011reported serious incident.",
  safeguarding_staffing_adequacy: "Unsafe staffing or supervision reported.",
  safeguarding_exit_continuity: "Unplanned exit or participant continuity risk reported."
};

const RISK_SIGNAL_REASONS = {
  risk_safeguarding_concern: "Serious safeguarding concern reported at the high-level composite signal.",
  risk_payment_concern: "Payment or claiming concern reported \u2014 requires consultant review.",
  risk_participant_transfer: "Participant transfer reported \u2014 continuity planning required."
};

function detectSafeguardingTriggers(answers) {
  const reasons = [];
  Object.entries(SAFEGUARDING_TRIGGER_REASONS).forEach(([qid, reason]) => {
    if (answers[qid] === "yes") reasons.push(reason);
  });
  Object.entries(RISK_SIGNAL_REASONS).forEach(([qid, reason]) => {
    if (answers[qid] === "yes") reasons.push(reason);
  });
  return reasons;
}

function detectScopeTriggers(answers) {
  const reasons = [];
  if (["unclear", "partial"].includes(answers.registration_pathway_clarity)) {
    reasons.push("Registration pathway is reported as unclear.");
  }
  if (answers.profile_service_focus === "mixed") {
    reasons.push("Mixed 0115 / 0138 service exposure was indicated.");
  }
  return reasons;
}

function detectConflictTriggers(answers, evidenceItems) {
  const reasons = [];
  if (
    answers.profile_registration_status === "registered" &&
    ["lapsed", "never_held"].includes(answers.registration_audit_status)
  ) {
    reasons.push("Conflicting answers: registered status reported without a current audit certificate.");
  }
  if (
    ["sil_0115", "sil_0138", "mixed"].includes(answers.profile_service_focus) &&
    answers.profile_registration_status === "unregistered_not_planning"
  ) {
    reasons.push("Conflicting answers: SIL delivery indicated without registration intent.");
  }
  const regEv = evidenceItems.find((i) => i.id === "ev_registration_profile");
  if (
    regEv &&
    regEv.rag === "red" &&
    ["registered", "in_process"].includes(answers.profile_registration_status)
  ) {
    reasons.push("Conflicting answers: registered status reported but registration evidence is missing or unclear.");
  }
  if (
    answers.sil_currently_delivering === "yes" &&
    answers.app_plan_continue === "yes" &&
    answers.app_plan_exit === "yes"
  ) {
    reasons.push("Conflicting answers: provider reports planning to BOTH continue and exit SIL delivery.");
  }
  if (
    answers.sil_currently_delivering === "yes" &&
    answers.profile_registration_status === "unregistered_not_planning"
  ) {
    reasons.push("Conflicting answers: currently delivering SIL but not planning to register.");
  }
  return reasons;
}

function detectCapacityTriggers(answers, evidenceItems, operationalRisks) {
  const reasons = [];
  const redEvidenceCount = evidenceItems.filter((i) => i.rag === "red").length;
  const highRiskCount = operationalRisks.filter((r) => r.level === "high").length;
  if (answers.profile_participant_scale === "51_plus" && (redEvidenceCount >= 3 || highRiskCount >= 2)) {
    reasons.push("Provider may be operating beyond current capability \u2014 high participant scale combined with multiple red-flag gaps.");
  }
  const safeguardingEvId = evidenceItems.find((i) => i.id === "ev_safeguarding_escalation");
  if (safeguardingEvId && safeguardingEvId.rag === "red") {
    reasons.push("Critical safeguarding escalation evidence is missing or unclear.");
  }
  return reasons;
}

function detectManualReview(answers, evidenceItems, operationalRisks) {
  const reasons = [
    ...detectSafeguardingTriggers(answers),
    ...detectScopeTriggers(answers),
    ...detectConflictTriggers(answers, evidenceItems),
    ...detectCapacityTriggers(answers, evidenceItems, operationalRisks),
    ...detectIntakeClarificationTriggers(answers),
    ...scanFreeText(answers.plan_notes || "").reasons
  ];
  return { manualReview: reasons.length > 0, manualReviewReasons: reasons };
}

// ---------------------------------------------------------------------------
// Phase 2 — provider category (kept for snapshot UI)
// ---------------------------------------------------------------------------

function determineCategory(answers, evidenceItems, operationalRisks, manualReview) {
  if (manualReview) {
    if (answers.profile_service_focus === "mixed") return PROVIDER_CATEGORIES.MIXED_MANUAL_REVIEW;
    return PROVIDER_CATEGORIES.UNKNOWN_MANUAL_REVIEW;
  }
  if (answers.plan_priority === "exit" || answers.app_plan_exit === "yes") return PROVIDER_CATEGORIES.SIL_EXIT_TRANSITION;
  const isRegistered =
    answers.profile_registration_status === "registered" || answers.profile_registration_status === "in_process";
  if (isRegistered) return PROVIDER_CATEGORIES.REGISTERED_UPLIFT;
  const isUnregistered = ["unregistered_considering", "unregistered_not_planning"].includes(
    answers.profile_registration_status
  );
  const isSil = ["sil_0115", "sil_0138"].includes(answers.profile_service_focus) || answers.sil_currently_delivering === "yes";
  if (isUnregistered && isSil) return PROVIDER_CATEGORIES.UNREGISTERED_SIL_PATHWAY;
  const hasIssues =
    evidenceItems.some((i) => i.rag === "red" || i.rag === "amber") ||
    operationalRisks.some((r) => r.level === "high" || r.level === "medium");
  if (!hasIssues && !isSil) return PROVIDER_CATEGORIES.REGISTERED_UPLIFT;
  return PROVIDER_CATEGORIES.UNKNOWN_MANUAL_REVIEW;
}

function buildActionPlan(category, evidenceItems, operationalRisks, manualReview) {
  if (manualReview) {
    return {
      d30: [
        "Pause non-essential changes to service delivery and document the status quo.",
        "Secure all current safeguarding, incident and medication records (read-only copies).",
        "Engage a qualified NDIS readiness consultant for a guided manual review."
      ],
      d60: [
        "Complete a structured root-cause review of the items flagged for manual review.",
        "Map every flagged concern to the responsible owner inside the organisation.",
        "Confirm whether any reportable matters require notification to the NDIS Commission."
      ],
      d90: [
        "Implement remediation actions emerging from the manual review, with documented evidence.",
        "Re-test policy-to-practice alignment on the items that triggered manual review.",
        "Decide registration / scope / continuity posture with consultant sign-off before audit."
      ]
    };
  }
  const redItems = evidenceItems.filter((i) => i.rag === "red");
  const amberItems = evidenceItems.filter((i) => i.rag === "amber");
  const highRisk = operationalRisks.filter((r) => r.level === "high");
  const mediumRisk = operationalRisks.filter((r) => r.level === "medium");
  const briefList = (arr) => arr.slice(0, 3).map((x) => x.title).join("; ") + (arr.length > 3 ? `; +${arr.length - 3} more` : "");

  switch (category.id) {
    case PROVIDER_CATEGORIES.SIL_EXIT_TRANSITION.id:
      return {
        d30: [
          "Draft a participant continuity statement (non-identifying) for each support arrangement.",
          "Inventory current contractual, funding and registration obligations.",
          "Confirm safeguarding cover during the transition window.",
          redItems.length ? `Open continuity-critical evidence packs for: ${briefList(redItems)}.` : null
        ].filter(Boolean),
        d60: [
          "Engage receiving providers, funders and key stakeholders with a documented handover schedule.",
          "Close out evidence: incidents, medication records, restrictive practice authorisations.",
          "Plan workforce continuity and exit obligations.",
          amberItems.length ? `Resolve the \"needs review\" items: ${briefList(amberItems)}.` : null
        ].filter(Boolean),
        d90: [
          "Execute handover with documented confirmation from receiving providers.",
          "Submit any required notifications relating to the change in service.",
          "Archive records in line with NDIS retention obligations."
        ]
      };
    case PROVIDER_CATEGORIES.UNREGISTERED_SIL_PATHWAY.id:
      return {
        d30: [
          "Map current services against the NDIS registration groups to determine the applicable class.",
          "Identify the audit type that applies (verification vs. certification).",
          "Stand up minimum viable governance: safeguarding, incident, medication, screening.",
          redItems.length ? `Prioritise missing evidence: ${briefList(redItems)}.` : null
        ].filter(Boolean),
        d60: [
          "Complete a self-assessment against the relevant NDIS Practice Standards modules.",
          "Close the highest-weight evidence gaps identified in this snapshot.",
          "Stage a documentation review with an experienced reader before approaching an auditor.",
          amberItems.length ? `Tighten evidence under review: ${briefList(amberItems)}.` : null
        ].filter(Boolean),
        d90: [
          "Lodge the registration application or move to a registered auditor engagement.",
          "Run a mock audit on a single participant journey (non-identifying scenario).",
          "Sign off on the readiness position with internal governance."
        ]
      };
    case PROVIDER_CATEGORIES.REGISTERED_UPLIFT.id:
    default: {
      const d30 = [
        "Confirm registration scope matches services actually delivered today.",
        "Diary the next audit window and the evidence horizon required for it."
      ];
      if (redItems.length > 0) d30.push(`Open remediation packs for missing evidence: ${briefList(redItems)}.`);
      if (highRisk.length > 0) d30.push(`Stand up immediate controls for: ${highRisk.map((r) => r.topic).join(", ")}.`);
      const d60 = [
        "Close documented-but-inconsistent gaps with practical workflow changes.",
        "Run a tracer audit on one service stream from referral through to incident review."
      ];
      if (amberItems.length > 0) d60.push(`Finalise evidence that needs review: ${briefList(amberItems)}.`);
      if (mediumRisk.length > 0) d60.push(`Tighten supervision and oversight on: ${mediumRisk.map((r) => r.topic).join(", ")}.`);
      const d90 = [
        "Conduct an internal readiness review against the official Evidence Guide indicators (readiness inference only — not what an auditor will conclude).",
        "Brief the board / leadership on residual risk and remediation owners.",
        "Lock the readiness pack ahead of the next external engagement."
      ];
      return { d30, d60, d90 };
    }
  }
}

// ---------------------------------------------------------------------------
// Phase 4 — Provider Pathway Classifier
// ---------------------------------------------------------------------------

export const PROVIDER_PATHWAYS = {
  SIL_EXITING: {
    id: "SIL_EXITING",
    title: "SIL provider exiting / transitioning",
    description: "Provider currently delivers SIL but has indicated an intention to exit or transition. Continuity planning is the priority."
  },
  SIL_0138_IN_PROGRESS: {
    id: "SIL_0138_IN_PROGRESS",
    title: "Existing SIL provider \u2014 0138 application in progress",
    description: "Provider holds or operates SIL services and has an active 0138 application or audit engagement underway."
  },
  SIL_0115_HOLDER_NEEDS_0138: {
    id: "SIL_0115_HOLDER_NEEDS_0138",
    title: "Existing 0115 SIL provider \u2014 0138 pathway review needed",
    description: "Provider currently delivers SIL under 0115 with no active 0138 application. Pathway clarification is the priority before 1 July 2026. Existing unregistered SIL providers who were delivering SIL before 1 July 2026 must be assessed against the 1 October 2026 application deadline."
  },
  UNREGISTERED_SIL: {
    id: "UNREGISTERED_SIL",
    title: "Unregistered SIL provider \u2014 registration pathway needed",
    description: "Provider delivers SIL without confirmed registration. A registration pathway review is the prudent first step. Existing unregistered SIL providers who were delivering SIL before 1 July 2026 must be assessed against the 1 October 2026 application deadline."
  },
  REGISTERED_NOT_SIL: {
    id: "REGISTERED_NOT_SIL",
    title: "Registered provider \u2014 not delivering SIL",
    description: "Provider is registered but does not currently deliver SIL. 0138 exposure is not applicable."
  },
  OUT_OF_SCOPE: {
    id: "OUT_OF_SCOPE",
    title: "Out of scope \u2014 not delivering SIL",
    description: "Provider is not delivering SIL and has no SIL-related obligations under 0138."
  },
  MANUAL_REVIEW: {
    id: "MANUAL_REVIEW",
    title: "Manual review required",
    description: "Automated pathway determination has been suspended. The case must be reviewed by a qualified NDIS consultant."
  }
};

function determineProviderPathway(answers, manualReview) {
  if (manualReview) return PROVIDER_PATHWAYS.MANUAL_REVIEW;
  const deliveringSil = answers.sil_currently_delivering === "yes";
  const planExit = answers.app_plan_exit === "yes";
  const has0115 = answers.provider_0115_held === "yes";
  const registered = answers.profile_registration_status === "registered" || answers.profile_registration_status === "in_process";
  const activeApp = ["lodged", "under_assessment", "audit_scheduled", "decision_received"].includes(answers.app_status);

  if (deliveringSil && planExit) return PROVIDER_PATHWAYS.SIL_EXITING;
  if (deliveringSil && activeApp) return PROVIDER_PATHWAYS.SIL_0138_IN_PROGRESS;
  if (deliveringSil && has0115) return PROVIDER_PATHWAYS.SIL_0115_HOLDER_NEEDS_0138;
  if (deliveringSil && !registered) return PROVIDER_PATHWAYS.UNREGISTERED_SIL;
  if (!deliveringSil && registered) return PROVIDER_PATHWAYS.REGISTERED_NOT_SIL;
  if (!deliveringSil) return PROVIDER_PATHWAYS.OUT_OF_SCOPE;
  return PROVIDER_PATHWAYS.MANUAL_REVIEW;
}

function compute0138Exposure(answers) {
  if (answers.sil_currently_delivering !== "yes") {
    return { level: "na", label: "Not applicable", description: "Provider is not currently delivering SIL." };
  }
  const criteria = [
    answers.sil_before_july_2026 === "yes",
    answers.sil_all_or_most_day === "yes",
    answers.sil_provider_manages === "yes",
    answers.sil_shared_living === "yes",
    answers.sil_provider_controls_tenancy === "yes"
  ];
  const score = criteria.filter(Boolean).length;
  const inhibitor = answers.sil_participant_self_manages === "yes";
  const adjusted = inhibitor ? Math.max(0, score - 1) : score;
  if (inhibitor && score < 3) {
    return { level: "low", label: "Low", description: "Participant self-manages workers \u2014 0138 exposure is limited.", score: adjusted };
  }
  if (adjusted >= 4) return { level: "high", label: "High", description: "Multiple SIL eligibility criteria are met. 0138 obligations apply.", score: adjusted };
  if (adjusted >= 2) return { level: "medium", label: "Medium", description: "Partial SIL eligibility criteria match \u2014 pathway clarification advised.", score: adjusted };
  return { level: "low", label: "Low", description: "Few SIL eligibility criteria match \u2014 confirm scope with a consultant.", score: adjusted };
}

function determineUrgency(answers, exposure, manualReview) {
  if (manualReview) return { level: "critical", label: "Critical", description: "Manual review is in effect \u2014 automated categorisation is on hold." };
  const deliveringSil = answers.sil_currently_delivering === "yes";
  const before2026 = answers.sil_before_july_2026 === "yes";
  const noApp = !answers.app_status || answers.app_status === "not_started" || answers.app_status === "not_planning";
  if (deliveringSil && before2026 && noApp) {
    return { level: "critical", label: "Critical", description: "Actively delivering SIL prior to 1 July 2026 with no registration application." };
  }
  if (deliveringSil && exposure.level === "high" && noApp) {
    return { level: "critical", label: "Critical", description: "High 0138 exposure with no active application." };
  }
  if (deliveringSil && exposure.level === "high") {
    return { level: "high", label: "High", description: "High 0138 exposure \u2014 evidence and audit readiness must be prioritised." };
  }
  if (deliveringSil && exposure.level === "medium") {
    return { level: "medium", label: "Medium", description: "Partial SIL eligibility \u2014 pathway clarification advised." };
  }
  if (!deliveringSil) {
    return { level: "low", label: "Low", description: "Not currently in 0138 scope." };
  }
  return { level: "medium", label: "Medium", description: "Review required to clarify pathway and obligations." };
}

const RECOMMENDED_OFFERS = {
  SIL_EXITING: {
    id: "OFFER_EXIT",
    title: "Exit and transition planning consult",
    description: "A structured engagement to plan participant continuity, notify stakeholders, and close out registration cleanly."
  },
  SIL_0138_IN_PROGRESS: {
    id: "OFFER_AUDIT_READINESS",
    title: "Pre-audit readiness review and evidence uplift",
    description: "Pre-audit review with evidence remediation packs against the items flagged red or amber in this snapshot."
  },
  SIL_0115_HOLDER_NEEDS_0138: {
    id: "OFFER_PATHWAY_CLARIFICATION",
    title: "0138 pathway clarification and scope review",
    description: "Targeted engagement to confirm 0138 application requirements and align scope before 1 July 2026."
  },
  UNREGISTERED_SIL: {
    id: "OFFER_REGISTRATION_PATHWAY",
    title: "Registration pathway consultation",
    description: "Scope, plan and stand up the governance documents required to lodge an application with an Approved Quality Auditor."
  },
  REGISTERED_NOT_SIL: {
    id: "OFFER_SCOPE_REVIEW",
    title: "Scope verification and registration review",
    description: "Verify current scope aligns to delivered services. Recommend a documentation pass on the items flagged in this snapshot."
  },
  OUT_OF_SCOPE: {
    id: "OFFER_NONE",
    title: "No 0138 service offer required at this time",
    description: "Provider is not in 0138 scope. Re-run this intake if service profile changes."
  },
  MANUAL_REVIEW: {
    id: "OFFER_MANUAL_REVIEW",
    title: "Manual review consultation with qualified consultant",
    description: "Suspend further automated steps. Engage a qualified NDIS readiness consultant for a guided review of the flagged items before any service decision."
  }
};

const NEXT_ACTIONS = {
  SIL_EXITING: "Book a transition-planning consult and draft non-identifying participant continuity statements this week.",
  SIL_0138_IN_PROGRESS: "Continue the application pathway and open remediation packs for the missing evidence listed in this snapshot.",
  SIL_0115_HOLDER_NEEDS_0138: "Open a 0138 pathway clarification engagement before 1 July 2026; confirm registration scope and audit type.",
  UNREGISTERED_SIL: "Open a registration pathway engagement immediately. Stand up safeguarding, incident, medication and screening documents first.",
  REGISTERED_NOT_SIL: "Confirm registration scope matches services delivered today; address any amber or red evidence items.",
  OUT_OF_SCOPE: "No further 0138 action required. Re-run this intake if service profile changes.",
  MANUAL_REVIEW: "Engage a qualified NDIS consultant for a guided manual review before any further service decisions."
};

export const BOUNDARY_STATEMENT =
  "NDIS 0138 Readiness is a consultancy readiness tool only. It is not legal advice, an Approved Quality Auditor report, an official NDIS Commission tool, a registration guarantee, or a compliance certification.";

// Source verification is a CONTROLLED OUTPUT, not a manual toggle.
// The four labels below are the only permitted values. The system derives the
// appropriate label from the assessment state. VERIFIED is reserved for cases
// where a future source register validates the regulatory points; today no
// such register is integrated, so VERIFIED is never auto-assigned.
const SOURCE_VERIFICATION_LABELS = {
  verified: {
    label: "VERIFIED",
    description:
      "This output has been corroborated against the source register. Reserved for points that the source-verification logic has independently confirmed."
  },
  requires_source_verification: {
    label: "REQUIRES SOURCE VERIFICATION",
    description:
      "Regulatory points in this output are self-reported and have not been independently verified. Confirm against the underlying source before relying on or publishing externally."
  },
  internal_only: {
    label: "INTERNAL ONLY",
    description:
      "This output is suitable for internal consultancy use only. It is not to be shared with the client or any external party in its current form."
  },
  do_not_publish: {
    label: "DO NOT PUBLISH",
    description:
      "Manual review or a safeguarding hold is in effect. This output must not be shared externally under any circumstances until a qualified consultant has completed a guided review."
  }
};

export function getSourceVerificationLabel(status) {
  return SOURCE_VERIFICATION_LABELS[status] || SOURCE_VERIFICATION_LABELS.requires_source_verification;
}

// Derive the correct controlled-output label for this assessment.
// Defers to the shared helper in lib/sourceVerification.js so the classifier
// chip and the SourceVerificationPanel always show the same status.
function deriveSourceVerificationStatusInternal(manualReview, pathwayId) {
  return determineSourceVerificationStatus({ manualReview, pathwayId });
}

function buildRisksRequiringReview(answers, evidenceItems, operationalRisks, manualReviewReasons) {
  const risks = [];
  manualReviewReasons.forEach((r) => risks.push({ id: `mr-${risks.length}`, severity: "high", source: "Manual review", label: r }));
  const RISK_SIGNAL_LABELS = {
    risk_safeguarding_concern: "Serious safeguarding concern reported",
    risk_payment_concern: "Payment / claiming concern reported",
    risk_participant_transfer: "Participant transfer required"
  };
  Object.entries(RISK_SIGNAL_LABELS).forEach(([qid, label]) => {
    if (answers[qid] === "yes" && !risks.some((r) => r.label.toLowerCase().includes(label.toLowerCase().slice(0, 20)))) {
      risks.push({ id: qid, severity: "high", source: "Risk signal", label });
    }
  });
  operationalRisks
    .filter((r) => r.level === "high")
    .forEach((r) => risks.push({ id: r.id, severity: "high", source: "Operational risk", label: `${r.topic}: ${r.label}` }));
  evidenceItems
    .filter((i) => i.rag === "red")
    .slice(0, 6)
    .forEach((i) => risks.push({ id: i.id, severity: "high", source: `Evidence (${i.domain})`, label: `Missing or unclear: ${i.title}` }));
  return risks;
}

function buildClassifierOutput(answers, classification) {
  const { manualReview, manualReviewReasons, evidenceItems, operationalRisks } = classification;
  const pathway = determineProviderPathway(answers, manualReview);
  const exposure = compute0138Exposure(answers);
  const urgency = determineUrgency(answers, exposure, manualReview);
  const sourceStatus = deriveSourceVerificationStatusInternal(manualReview, pathway.id);
  const sourceMeta = getSourceVerificationLabel(sourceStatus);
  const recommendedOffer = RECOMMENDED_OFFERS[pathway.id];
  const evidenceNeeded = [
    ...evidenceItems.filter((i) => i.rag === "red"),
    ...evidenceItems.filter((i) => i.rag === "amber")
  ];
  const risksRequiringReview = buildRisksRequiringReview(answers, evidenceItems, operationalRisks, manualReviewReasons);
  return {
    pathway,
    exposure,
    urgency,
    sourceVerification: { status: sourceStatus, ...sourceMeta },
    recommendedOffer,
    evidenceNeeded,
    risksRequiringReview,
    boundaryStatement: BOUNDARY_STATEMENT,
    nextAction: NEXT_ACTIONS[pathway.id]
  };
}

// ---------------------------------------------------------------------------
// QC additions — intake clarification triggers & readiness signals
// ---------------------------------------------------------------------------
// These helpers surface QC-02, QC-03, QC-04, QC-05, QC-08 without touching
// scoring weights, evidence-state logic, or converting missing answers to no.
//   - QC-02 sil_all_or_most_day "unsure" -> clarification / manual review
//   - QC-03 sil_provider_manages "shared" or "unsure" -> clarification /
//     manual review; not numerically scored (compute0138Exposure keeps its
//     strict `=== "yes"` test, so shared/unsure are neither scored as yes
//     nor coerced to no)
//   - QC-04 sil_package_daily_life_purpose  -> readiness signal only
//   - QC-05 practice_supported_decision_making -> readiness/evidence signal
//     only, explicitly excluded from operational-risk scoring
//   - QC-08 ld_housing_security_provider_choice -> readiness/referral signal
//     only, never a tenancy-law determination
// Missing responses render as "Not yet assessed" — they are NOT converted to
// "no" anywhere in the pipeline.

function detectIntakeClarificationTriggers(answers) {
  const reasons = [];
  if (answers.sil_currently_delivering !== "yes") return reasons;
  if (answers.sil_all_or_most_day === "unsure") {
    reasons.push(
      "SIL frequency (all or most of the day) is reported as unsure \u2014 clarification required before pathway categorisation."
    );
  }
  if (answers.sil_provider_manages === "shared") {
    reasons.push(
      "SIL provider-managed workforce reported as a shared arrangement \u2014 clarification required before pathway categorisation."
    );
  }
  if (answers.sil_provider_manages === "unsure") {
    reasons.push(
      "SIL provider-managed workforce is reported as unsure \u2014 clarification required before pathway categorisation."
    );
  }
  return reasons;
}

const READINESS_SIGNAL_FIELDS = [
  {
    id: "sil_package_daily_life_purpose",
    label: "SIL package supports daily life and purpose (QC-04)",
    section: "SIL delivery"
  },
  {
    id: "practice_supported_decision_making",
    label: "Supported decision-making practice (QC-05)",
    section: "SIL delivery"
  },
  {
    id: "ld_housing_security_provider_choice",
    label: "Housing security & genuine provider choice (QC-08)",
    section: "Legal & documentation referral"
  }
];

const READINESS_SIGNAL_RESPONSE_LABELS = {
  yes: "Yes",
  partly: "Partly",
  no: "No",
  unsure: "Unsure",
  shared: "Shared",
  na: "Not applicable"
};

function collectReadinessSignals(answers) {
  return READINESS_SIGNAL_FIELDS.map((field) => {
    const raw = answers ? answers[field.id] : undefined;
    const provided =
      raw !== undefined && raw !== null && String(raw).trim() !== "";
    const responseLabel = provided
      ? (READINESS_SIGNAL_RESPONSE_LABELS[raw] || String(raw))
      : "Not yet assessed";
    return {
      id: field.id,
      label: field.label,
      section: field.section,
      response: provided ? raw : null,
      responseLabel,
      provided,
      evidenceStatus: "Downstream evidence verification",
      interpretation:
        "Readiness / referral signal only \u2014 not a scoring, compliance, certification, or registration-guarantee determination.",
      readinessFinding: provided
        ? `Provider response recorded (${responseLabel}). Interpretation stays with the consultant; evidence verification stays downstream.`
        : "Not yet assessed."
    };
  });
}

// ---------------------------------------------------------------------------
// Top-level classifier
// ---------------------------------------------------------------------------

export function classifyAssessment(answers) {
  const evidenceItems = collectEvidenceItems(answers);
  const domainRollup = collectDomainRollup(evidenceItems);
  const operationalRisks = collectOperationalRisks(answers);
  const { manualReview, manualReviewReasons } = detectManualReview(answers, evidenceItems, operationalRisks);
  const category = determineCategory(answers, evidenceItems, operationalRisks, manualReview);
  const actionPlan = buildActionPlan(category, evidenceItems, operationalRisks, manualReview);
  const missingEvidence = evidenceItems.filter((i) => i.rag === "red");
  const reviewEvidence = evidenceItems.filter((i) => i.rag === "amber");
  const readinessSignals = collectReadinessSignals(answers);
  const partial = {
    manualReview,
    manualReviewReasons,
    category,
    evidenceItems,
    domainRollup,
    missingEvidence,
    reviewEvidence,
    operationalRisks,
    actionPlan,
    readinessSignals
  };
  const classifier = buildClassifierOutput(answers, partial);
  const sourceVerification = buildSourceVerification({ classifier, result: partial });
  return { ...partial, classifier, sourceVerification };
}

// ---------------------------------------------------------------------------
// Report helpers (Phase 3, extended for Phase 4)
// ---------------------------------------------------------------------------

const PROFILE_SERVICE_LABELS = {
  sil_0115: "SIL \u2014 Support Independent Living (0115) only",
  sil_0138: "Specialised supports under 0138 only",
  mixed: "Mixed 0115 and 0138 supports",
  other: "Other community supports only",
  unsure: "Unclear or unsure"
};
const PROFILE_REGISTRATION_LABELS = {
  registered: "Currently registered NDIS provider",
  unregistered_considering: "Unregistered, considering registration",
  unregistered_not_planning: "Unregistered, not planning to register",
  in_process: "In an active registration or audit process",
  unsure: "Unclear or unsure"
};
const PROFILE_SCALE_LABELS = {
  "1_5": "1 \u2013 5 participants",
  "6_20": "6 \u2013 20 participants",
  "21_50": "21 \u2013 50 participants",
  "51_plus": "51 + participants",
  undisclosed: "Scale not disclosed"
};
const MIXED_FOCUS_LABELS = {
  mixed: "Yes \u2014 mixed 0115 / 0138 exposure indicated",
  unsure: "Possible \u2014 service focus reported as unclear"
};
const RISK_LEVEL_LABELS = {
  ok: "In place",
  low: "Low",
  medium: "Medium",
  high: "High",
  na: "N/A",
  unknown: "Unanswered"
};
const RAG_LABELS = { green: "GREEN", amber: "AMBER", red: "RED", na: "N/A", unknown: "UNKNOWN" };

export function getProviderSnapshot(answers, result) {
  const serviceFocus = answers.profile_service_focus;
  const providerType = PROFILE_SERVICE_LABELS[serviceFocus] || "Not indicated";
  const registrationPosition =
    PROFILE_REGISTRATION_LABELS[answers.profile_registration_status] || "Not indicated";
  const mixedUncertainty = serviceFocus === "mixed" || serviceFocus === "unsure";
  const mixedLabel = MIXED_FOCUS_LABELS[serviceFocus] || "No \u2014 not indicated";
  const participantScale = PROFILE_SCALE_LABELS[answers.profile_participant_scale] || "Not indicated";
  return {
    providerName: answers.provider_name || "\u2014",
    providerAbn: answers.provider_abn || "\u2014",
    providerType,
    registrationPosition,
    mixedUncertainty,
    mixedLabel,
    participantScale,
    preliminaryCategory: result.category.title,
    manualReviewStatus: result.manualReview ? "Manual Review Required" : "Preliminary categorisation",
    manualReview: result.manualReview
  };
}

export function buildExecutiveSummary(answers, result) {
  if (result.manualReview) {
    return [
      "This case has been routed to Manual Review Required because one or more safeguarding, governance, or scope triggers were identified in the intake. Automated categorisation has been suspended and should not be relied on until a qualified consultant has completed a guided review.",
      "The 30 / 60 / 90 day plan that follows is a conservative holding sequence intended to stabilise the organisation while that review takes place. NDIS 0138 Readiness is a consultancy readiness tool only \u2014 it is not legal advice, an Approved Quality Auditor report, an official NDIS Commission tool, a registration guarantee, or a compliance certification.",
      "This executive summary, and every other section of this report, remains preliminary."
    ].join("\n\n");
  }
  const redCount = result.missingEvidence.length;
  const amberCount = result.reviewEvidence.length;
  const highRiskCount = result.operationalRisks.filter((r) => r.level === "high").length;
  const mediumRiskCount = result.operationalRisks.filter((r) => r.level === "medium").length;
  const redDomains = result.domainRollup.filter((d) => d.rag === "red").length;
  const amberDomains = result.domainRollup.filter((d) => d.rag === "amber").length;
  const greenDomains = result.domainRollup.filter((d) => d.rag === "green").length;
  const pathway = result.classifier ? result.classifier.pathway.title : result.category.title;
  const paragraphs = [];
  paragraphs.push(BOUNDARY_STATEMENT);
  paragraphs.push(
    `Based on the responses provided, the organisation aligns with the preliminary pathway \u201c${pathway}\u201d. The R.E.A.D.Y. domain rollup identifies ${redDomains} domain${redDomains === 1 ? "" : "s"} rated red, ${amberDomains} amber, and ${greenDomains} green across the 17-item evidence map.`
  );
  if (redCount === 0 && amberCount === 0) {
    paragraphs.push(
      highRiskCount > 0
        ? `Across the evidence map, no items were marked as missing, unconfirmed, or needing review. Operational risk indicators flag ${highRiskCount} high-priority area${highRiskCount === 1 ? "" : "s"} and ${mediumRiskCount} at medium.`
        : "Across the evidence map, no items were marked as missing, unconfirmed, or needing review. Operational risk indicators are within acceptable bounds for this preliminary review."
    );
  } else {
    paragraphs.push(
      `Across the evidence map, ${redCount} item${redCount === 1 ? " is" : "s are"} missing or unconfirmed, and ${amberCount} item${amberCount === 1 ? " exists" : "s exist"} but require review. Operational risk indicators flag ${highRiskCount} high-priority area${highRiskCount === 1 ? "" : "s"} and ${mediumRiskCount} at medium.`
    );
  }
  paragraphs.push(
    "The 30 / 60 / 90 day plan that follows is a structured suggestion only. It is intended to be sequenced with the support of a qualified readiness consultant and confirmed against the organisation's specific operating context."
  );
  paragraphs.push("This summary remains preliminary and is intended to support consultation, not replace it.");
  return paragraphs.join("\n\n");
}

function evidenceLevelToRiskLevel(rag) {
  if (rag === "green") return "ok";
  if (rag === "amber") return "medium";
  if (rag === "red") return "high";
  if (rag === "na") return "na";
  return "unknown";
}

const REPORT_RISK_AREAS = [
  { key: "staffing", title: "Staffing", from: "risk", id: "risk_staffing_supervision" },
  { key: "supervision", title: "Supervision", from: "evidence", id: "ev_rostering_supervision" },
  { key: "rostering", title: "Rostering", from: "evidence", id: "ev_rostering_supervision" },
  { key: "handover", title: "Shift handover", from: "evidence", id: "ev_handover" },
  { key: "medication", title: "Medication support", from: "evidence", id: "ev_medication_process" },
  { key: "documentation", title: "Documentation \u00b7 policy\u2013practice alignment", from: "risk", id: "risk_practice_gap" },
  { key: "incident_management", title: "Incident management", from: "evidence", id: "ev_incident_management" },
  { key: "complaints", title: "Complaints management", from: "evidence", id: "ev_complaints_management" },
  { key: "continuity", title: "Continuity of support", from: "risk", id: "risk_continuity_concerns" },
  { key: "exit_transition", title: "Exit or transition planning", from: "evidence", id: "ev_exit_transition_planning" }
];

export function getReportRiskAreas(result) {
  return REPORT_RISK_AREAS.map((area) => {
    if (area.from === "risk") {
      const row = result.operationalRisks.find((r) => r.id === area.id);
      if (!row) return { ...area, level: "unknown", label: "Not answered" };
      return { ...area, level: row.level, label: row.label };
    }
    const item = result.evidenceItems.find((i) => i.id === area.id);
    if (!item) return { ...area, level: "unknown", label: "Not answered" };
    return { ...area, level: evidenceLevelToRiskLevel(item.rag), label: item.label };
  });
}

// Design Spec 001 Phase 3 — derived, read-only Clarification Required list.
// Purely presentational: never feeds scoring, classification, or verification.
export function clarificationRequiredItems(result) {
  return (result?.evidenceItems || []).filter((i) => i.applicability === "needs_clarification");
}

export function evidenceShortNote(item) {
  switch (item.answer) {
    case "current":
      return "Reported current and operational \u2014 spot-check in next internal audit.";
    case "review":
      return "Reported present but needs review \u2014 schedule a documentation pass before next audit.";
    case "missing":
      return "Reported missing \u2014 open a remediation pack and assign an owner.";
    case "unsure":
      return "Needs Clarification \u2014 more information, evidence, or context is required before a readiness judgement can be made. Remains visible as a readiness signal.";
    case "na":
      // Phase 8D — never surface the raw not-applicable reason in
      // client-facing output. Confirmation must be a synthesis line only.
      return "Marked not applicable \u2014 confirm the exclusion is consistent with the registration scope on record.";
    default:
      return "Not yet answered \u2014 complete the intake to refine this item.";
  }
}

// ---------------------------------------------------------------------------
// Phase 8D — Deterministic Professional Finding synthesis.
//
// Client-facing output layers must never leak raw customer comments,
// consultantNotes, evidenceNotes, notApplicableReasons, prompt text or
// internal reasoning. This function produces a bounded, participant-centred
// professional finding from structured fields only. It reads nothing beyond:
//   - item.answer            (canonical evidence answer)
//   - item.canonicalState    (derived canonical state label)
//   - item.applicability     (Design Spec 001)
//   - item.fieldVerification (P2 per-field state)
//   - item.title / item.domain
// It NEVER reads item.evidenceNote, item.notApplicableReason, or any
// answers[<id>__evidence_note] / answers[<id>__na_reason] value.
// ---------------------------------------------------------------------------
const FINDING_BASIS = {
  current: "Provider reports the required evidence is current and operational.",
  review: "Provider reports the evidence exists but requires documentation review before audit reliance.",
  missing: "Provider reports the required evidence is not in place.",
  unsure: "Provider position on this evidence item is not yet clear.",
  na: "Provider has marked this item as outside the applicable scope of this readiness review.",
  unanswered: "This evidence item has not been answered during intake."
};

const FINDING_IMPLICATION = {
  current: "Readiness signal is positive for this item and can be spot-verified during the next internal audit.",
  review: "Readiness signal is amber; the item cannot be relied on until a documentation review is completed.",
  missing: "Readiness signal is red; this item is a readiness gap and must be closed before further audit exposure.",
  unsure: "A readiness judgement cannot be issued for this item until clarification is obtained.",
  na: "No readiness implication if the exclusion is consistent with the registration scope.",
  unanswered: "No readiness judgement can be issued until the intake response is completed."
};

const FINDING_ACTION = {
  current: "Include this item in the next internal audit spot-check cycle.",
  review: "Schedule a documentation review pass and assign an accountable owner.",
  missing: "Open a remediation pack for this item, assign an owner and set a target completion date.",
  unsure: "Return to the provider for clarification and re-status the item on receipt of the additional information.",
  na: "Confirm the exclusion is consistent with the registration scope on record.",
  unanswered: "Complete the outstanding intake response for this item to enable a readiness finding."
};

function findingReferral(answer, fieldVerification) {
  if (answer === "missing") {
    return "Referral advisable to the responsible internal owner (and, where relevant, an external subject-matter reviewer) if the gap remains after remediation.";
  }
  if (answer === "unsure") {
    return "Referral advisable to the responsible internal reviewer to resolve the clarification requirement.";
  }
  if (fieldVerification === "source_required") {
    return "A supporting source should be identified for this item before reliance.";
  }
  return "";
}

export function professionalFinding(item) {
  if (!item) return "";
  const key = item.answer || "unanswered";
  const basis = FINDING_BASIS[key] || FINDING_BASIS.unanswered;
  const implication = FINDING_IMPLICATION[key] || FINDING_IMPLICATION.unanswered;
  const action = FINDING_ACTION[key] || FINDING_ACTION.unanswered;
  const referral = findingReferral(key, item.fieldVerification);
  const canonical = item.canonicalState
    ? `Canonical state: ${item.canonicalState}.`
    : "Canonical state: not yet derived.";
  const parts = [basis, implication, action];
  if (referral) parts.push(referral);
  parts.push(canonical);
  return parts.join(" ");
}

function levelLabel(level) {
  return RISK_LEVEL_LABELS[level] || "Unknown";
}

// ---------------------------------------------------------------------------
// Plain-text + markdown report builders
// ---------------------------------------------------------------------------

function classifierToPlainText(classifier) {
  const lines = [];
  lines.push("-------------------------------------------------------------------------");
  lines.push(`  Provider pathway          : ${classifier.pathway.title}`);
  lines.push(`                              ${classifier.pathway.description}`);
  lines.push(`  0138 exposure likelihood  : ${classifier.exposure.label} \u2014 ${classifier.exposure.description}`);
  lines.push(`  Urgency rating            : ${classifier.urgency.label} \u2014 ${classifier.urgency.description}`);
  lines.push(`  Source verification       : ${classifier.sourceVerification.label} \u2014 ${classifier.sourceVerification.description}`);
  lines.push(`  Recommended service offer : ${classifier.recommendedOffer.title}`);
  lines.push(`                              ${classifier.recommendedOffer.description}`);
  lines.push("");
  lines.push("  Evidence needed:");
  if (classifier.evidenceNeeded.length === 0) {
    lines.push("    None recorded.");
  } else {
    classifier.evidenceNeeded.forEach((i) =>
      lines.push(`    \u2022 [${i.domain}] ${i.title} \u2014 ${i.label}`)
    );
  }
  lines.push("");
  lines.push("  Risks requiring review:");
  if (classifier.risksRequiringReview.length === 0) {
    lines.push("    None recorded.");
  } else {
    classifier.risksRequiringReview.forEach((r) => lines.push(`    \u2022 [${r.source}] ${r.label}`));
  }
  lines.push("");
  lines.push(`  Boundary statement        : ${classifier.boundaryStatement}`);
  lines.push(`  Next action               : ${classifier.nextAction}`);
  return lines.join("\n");
}

function classifierToMarkdown(classifier) {
  const md = [];
  md.push("| Field | Value |");
  md.push("| --- | --- |");
  md.push(`| Provider pathway | **${classifier.pathway.title}** \u2014 ${classifier.pathway.description} |`);
  md.push(`| 0138 exposure likelihood | **${classifier.exposure.label}** \u2014 ${classifier.exposure.description} |`);
  md.push(`| Urgency rating | **${classifier.urgency.label}** \u2014 ${classifier.urgency.description} |`);
  md.push(`| Source verification status | **${classifier.sourceVerification.label}** \u2014 ${classifier.sourceVerification.description} |`);
  md.push(`| Recommended service offer | **${classifier.recommendedOffer.title}** \u2014 ${classifier.recommendedOffer.description} |`);
  md.push(`| Boundary statement | ${classifier.boundaryStatement} |`);
  md.push(`| Next action | ${classifier.nextAction} |`);
  md.push("");
  md.push("### Evidence needed");
  if (classifier.evidenceNeeded.length === 0) md.push("_None recorded._");
  else classifier.evidenceNeeded.forEach((i) => md.push(`- **${i.title}** _(domain ${i.domain})_ \u2014 ${i.label}`));
  md.push("");
  md.push("### Risks requiring review");
  if (classifier.risksRequiringReview.length === 0) md.push("_None recorded._");
  else classifier.risksRequiringReview.forEach((r) => md.push(`- _${r.source}_ \u2014 ${r.label}`));
  md.push("");
  return md.join("\n");
}

function sourceVerificationToPlainText(sv) {
  const lines = [];
  lines.push("-------------------------------------------------------------------------");
  lines.push(`  Status                    : ${sv.statusLabel}`);
  lines.push(`  Last reviewed              : ${sv.lastReviewedDate || "\u2014"}`);
  lines.push(`  Source basis               : ${sv.sourceBasis}`);
  lines.push("");
  lines.push("  Official sources relied on:");
  if (sv.officialSources.length === 0) {
    lines.push("    None mapped.");
  } else {
    sv.officialSources.forEach((s) =>
      lines.push(
        `    \u2022 [${s.status}] ${s.title} (reviewed ${s.lastReviewedDate}${s.url ? `; ${s.url}` : ""})`
      )
    );
  }
  lines.push("");
  lines.push("  Internal & founding sources relied on:");
  if (sv.internalSources.length === 0) {
    lines.push("    None recorded.");
  } else {
    sv.internalSources.forEach((s) =>
      lines.push(
        `    \u2022 [${s.status}] ${s.title} (reviewed ${s.lastReviewedDate}${s.url ? `; ${s.url}` : ""})`
      )
    );
  }
  lines.push("");
  lines.push("  Requires source verification \u2014 notes:");
  sv.requiresVerificationNotes.forEach((n) => lines.push(`    \u2022 ${n}`));
  lines.push("");
  lines.push(`  Boundary statement         : ${sv.boundaryStatement}`);
  return lines.join("\n");
}

function sourceVerificationToMarkdown(sv) {
  const md = [];
  md.push("| Field | Value |");
  md.push("| --- | --- |");
  md.push(`| Status | **${sv.statusLabel}** |`);
  md.push(`| Last reviewed | ${sv.lastReviewedDate || "\u2014"} |`);
  md.push(`| Source basis | ${sv.sourceBasis} |`);
  md.push(`| Boundary statement | ${sv.boundaryStatement} |`);
  md.push("");
  md.push("### Official sources relied on");
  if (sv.officialSources.length === 0) md.push("_None mapped._");
  else
    sv.officialSources.forEach((s) =>
      md.push(
        `- **${s.title}** _(authority ${s.authorityLevel}, status ${s.status}, reviewed ${s.lastReviewedDate})_${s.url ? ` — [${s.url}](${s.url})` : ""}`
      )
    );
  md.push("");
  md.push("### Internal & founding sources relied on");
  if (sv.internalSources.length === 0) md.push("_None recorded._");
  else
    sv.internalSources.forEach((s) =>
      md.push(
        `- **${s.title}** _(authority ${s.authorityLevel}, status ${s.status}, reviewed ${s.lastReviewedDate})_${s.url ? ` — [${s.url}](${s.url})` : ""}`
      )
    );
  md.push("");
  md.push("### Requires source verification — notes");
  sv.requiresVerificationNotes.forEach((n) => md.push(`- ${n}`));
  md.push("");
  return md.join("\n");
}

export function buildMarkdownReport({ result, answers, assessmentId, generatedAt, readinessActions }) {
  const provider = getProviderSnapshot(answers, result);
  const exec = buildExecutiveSummary(answers, result);
  const riskAreas = getReportRiskAreas(result);
  const md = [];
  md.push("# Readiness Snapshot Report");
  md.push("");
  md.push(`**Reference:** ${assessmentId}  `);
  md.push(`**Generated:** ${generatedAt}  `);
  md.push(`**Framework:** R.E.A.D.Y. v0.1  `);
  md.push(`**Status:** ${provider.manualReviewStatus}`);
  md.push("");
  md.push("> _NDIS 0138 Readiness is a consultancy readiness tool only. It is not legal advice, an Approved Quality Auditor report, an official NDIS Commission tool, a registration guarantee, or a compliance certification._");
  md.push("");
  if (result.manualReview) {
    md.push("---");
    md.push("");
    md.push("## \u26a0\ufe0e Manual Review Required");
    md.push("");
    md.push("Automated scoring has been suspended. Do not act on the categorisation below without a guided review by a qualified NDIS consultant.");
    md.push("");
    md.push("**Reasons:**");
    md.push("");
    result.manualReviewReasons.forEach((r) => md.push(`- ${r}`));
    md.push("");
  }
  md.push("---");
  md.push("");
  md.push("## 01 / Provider Snapshot");
  md.push("");
  md.push("| Field | Value |");
  md.push("| --- | --- |");
  md.push(`| Provider organisation | ${provider.providerName} |`);
  md.push(`| ABN | ${provider.providerAbn} |`);
  md.push(`| Provider type | ${provider.providerType} |`);
  md.push(`| Registration position | ${provider.registrationPosition} |`);
  md.push(`| Mixed 0115 / 0138 exposure | ${provider.mixedLabel} |`);
  md.push(`| Participant scale | ${provider.participantScale} |`);
  md.push(`| Preliminary provider category | ${provider.preliminaryCategory} |`);
  md.push(`| Manual review status | ${provider.manualReviewStatus} |`);
  md.push("");
  if (result.classifier) {
    md.push("## 02 / Classifier Output");
    md.push("");
    md.push(classifierToMarkdown(result.classifier));
  }
  if (result.sourceVerification) {
    md.push("## 03 / Source Verification");
    md.push("");
    md.push(sourceVerificationToMarkdown(result.sourceVerification));
  }
  md.push("## 04 / Executive Summary");
  md.push("");
  md.push(exec);
  md.push("");
  md.push("## 05 / R.E.A.D.Y. Summary");
  md.push("");
  md.push("| Domain | Status | Green | Amber | Red | N/A |");
  md.push("| --- | --- | --- | --- | --- | --- |");
  result.domainRollup.forEach((d) => {
    md.push(
      `| ${d.key} \u2014 ${d.title} | ${RAG_LABELS[d.rag] || "UNKNOWN"} | ${d.counts.green || 0} | ${d.counts.amber || 0} | ${d.counts.red || 0} | ${d.counts.na || 0} |`
    );
  });
  md.push("");
  md.push("## 06 / Evidence Readiness Table");
  md.push("");
  md.push("| # | Item | Domain | Answer | Status | Canonical state | Finding |");
  md.push("| --- | --- | --- | --- | --- | --- | --- |");
  result.evidenceItems.forEach((item) => {
    const fv = item.fieldVerificationLabel ? ` Field verification: ${item.fieldVerificationLabel}.` : "";
    const finding = professionalFinding(item).replace(/\|/g, "/");
    md.push(
      `| ${String(item.number).padStart(2, "0")} | ${item.title} | ${item.domain} | ${item.shortLabel} | ${RAG_LABELS[item.rag] || "UNKNOWN"} | ${item.canonicalState || "\u2014"} | ${finding}${fv} |`
    );
  });
  md.push("");
  md.push("## 07 / Missing Evidence");
  md.push("");
  if (result.missingEvidence.length === 0) md.push("_No missing or unconfirmed evidence recorded._");
  else result.missingEvidence.forEach((i) => md.push(`- **${i.title}** _(domain ${i.domain}, item ${String(i.number).padStart(2, "0")})_ \u2014 ${evidenceShortNote(i)}`));
  md.push("");
  md.push("## 08 / Evidence Needing Review");
  md.push("");
  if (result.reviewEvidence.length === 0) md.push("_No items flagged for review._");
  else result.reviewEvidence.forEach((i) => md.push(`- **${i.title}** _(domain ${i.domain}, item ${String(i.number).padStart(2, "0")})_ \u2014 ${evidenceShortNote(i)}`));
  md.push("");
  md.push("## 09 / Operational Risk Summary");
  md.push("");
  md.push("| Area | Status | Note |");
  md.push("| --- | --- | --- |");
  riskAreas.forEach((area) => md.push(`| ${area.title} | ${levelLabel(area.level)} | ${area.label} |`));
  md.push("");
  md.push("## 10 / 30 / 60 / 90 Day Plan");
  md.push("");
  md.push("### Days 0\u201330 \u2014 Stabilise & identify");
  result.actionPlan.d30.forEach((a) => md.push(`- ${a}`));
  md.push("");
  md.push("### Days 31\u201360 \u2014 Repair & prepare");
  result.actionPlan.d60.forEach((a) => md.push(`- ${a}`));
  md.push("");
  md.push("### Days 61\u201390 \u2014 Review & verify");
  result.actionPlan.d90.forEach((a) => md.push(`- ${a}`));
  md.push("");
  md.push("## 11 / Professional Readiness Findings");
  md.push("");
  md.push("_Deterministic synthesis of the material readiness findings from this review. Raw consultant reasoning, customer comments and evidence notes are deliberately excluded from this client-facing section._");
  md.push("");
  const mdFindingsItems = (result.evidenceItems || []).filter(
    (i) => i.answer === "missing" || i.answer === "review" || i.answer === "unsure"
  );
  if (result.manualReview) {
    md.push("_Automated categorisation is on hold pending manual consultant review. A synthesised readiness finding cannot be issued for this provider until manual review is complete._");
  } else {
    const mdMissing = (result.evidenceItems || []).filter((i) => i.answer === "missing").length;
    const mdReview = (result.evidenceItems || []).filter((i) => i.answer === "review").length;
    const mdClarify = (result.evidenceItems || []).filter((i) => i.answer === "unsure").length;
    md.push(
      `Across ${result.evidenceItems.length} evidence items assessed, ${mdMissing} were reported as missing, ${mdReview} require documentation review, and ${mdClarify} require clarification before a readiness judgement can be issued.`
    );
    md.push("");
    if (mdFindingsItems.length === 0) {
      md.push("_No material readiness findings — every evidence item was reported as in place. Spot verification is recommended at the next internal audit cycle._");
    } else {
      mdFindingsItems.forEach((i) =>
        md.push(
          `- **${i.title}** _(domain ${i.domain}, item ${String(i.number).padStart(2, "0")})_ \u2014 ${professionalFinding(i)}`
        )
      );
    }
  }
  md.push("");
  md.push("## 12 / Clarification Required");
  md.push("");
  md.push("_Items needing more information, evidence, or context before a readiness judgement can be made. Clarification is a readiness signal, not blame or non-compliance._");
  md.push("");
  const mdClarification = clarificationRequiredItems(result);
  if (mdClarification.length === 0) md.push("_None — no items are currently awaiting clarification._");
  else mdClarification.forEach((i) => md.push(`- **${i.title}** _(domain ${i.domain}, item ${String(i.number).padStart(2, "0")})_ \u2014 Needs Clarification`));
  md.push("");
  md.push("## 12b / Readiness Signals");
  md.push("");
  md.push("_Readiness / referral signals gathered at intake (QC-04, QC-05, QC-08). Provider response, evidence status, consultant interpretation and readiness finding are held as separate columns. No pass/fail, compliance, certification or registration-guarantee outcome is derived from this table._");
  md.push("");
  const mdSignals = (result && result.readinessSignals) || [];
  if (mdSignals.length === 0) {
    md.push("_No readiness signals in scope for this assessment._");
  } else {
    md.push("| Signal | Provider response | Evidence status | Consultant interpretation | Readiness finding |");
    md.push("| --- | --- | --- | --- | --- |");
    mdSignals.forEach((s) => {
      md.push(`| ${s.label} | ${s.responseLabel} | ${s.evidenceStatus} | ${s.interpretation} | ${s.readinessFinding} |`);
    });
  }
  md.push("");
  md.push("## 13 / Readiness Actions");
  md.push("");
  md.push("_Consultant-recorded improvement and clarification steps. Not audit findings, failures, or compliance declarations._");
  md.push("");
  const mdActions = readinessActions || [];
  if (mdActions.length === 0) md.push("_No readiness actions recorded._");
  else mdActions.forEach((a, idx) => md.push(`${idx + 1}. ${a.text}`));
  md.push("");
  md.push("## 14 / Legal & Documentation Referral Triggers");
  md.push("");
  md.push("_Readiness indicators, evidence gaps and referral triggers synthesised from provider responses. Not a legal opinion, audit determination, or regulator outcome. Raw customer comments and internal consultant notes are deliberately excluded._");
  md.push("");
  const mdLegalRows = buildLegalReferralRows(answers);
  md.push("| Area checked | Evidence requested | Evidence status | Red flag | Referral trigger | Suggested next action |");
  md.push("| --- | --- | --- | --- | --- | --- |");
  mdLegalRows.forEach((r) => {
    md.push(`| ${r.area} | ${r.evidenceRequested} | ${r.evidenceStatus} | ${r.redFlag} | ${r.referralTrigger} | ${r.suggestedNextAction} |`);
  });
  md.push("");
  md.push("---");
  md.push("");
  md.push("_End of report. Held in local browser state only. No participant identifiers, addresses, medical histories, or worker private information are collected by this tool._");
  return md.join("\n");
}

export function buildPlainTextReport({ result, answers, assessmentId, generatedAt, readinessActions }) {
  const provider = getProviderSnapshot(answers, result);
  const exec = buildExecutiveSummary(answers, result);
  const riskAreas = getReportRiskAreas(result);
  const lines = [];
  lines.push("READINESS SNAPSHOT REPORT");
  lines.push("=========================================================================");
  lines.push(`Reference:  ${assessmentId}`);
  lines.push(`Generated:  ${generatedAt}`);
  lines.push("Framework:  R.E.A.D.Y. v0.1");
  lines.push(`Status:     ${provider.manualReviewStatus}`);
  lines.push("");
  lines.push("DISCLAIMER");
  lines.push(BOUNDARY_STATEMENT);
  lines.push("");
  if (result.manualReview) {
    lines.push("-------------------------------------------------------------------------");
    lines.push("!! MANUAL REVIEW REQUIRED \u2014 SCORING ON HOLD");
    lines.push("-------------------------------------------------------------------------");
    result.manualReviewReasons.forEach((r) => lines.push(`  \u2022 ${r}`));
    lines.push("");
  }
  lines.push("01 / PROVIDER SNAPSHOT");
  lines.push("-------------------------------------------------------------------------");
  lines.push(`  Provider organisation     : ${provider.providerName}`);
  lines.push(`  ABN                       : ${provider.providerAbn}`);
  lines.push(`  Provider type             : ${provider.providerType}`);
  lines.push(`  Registration position     : ${provider.registrationPosition}`);
  lines.push(`  Mixed 0115 / 0138         : ${provider.mixedLabel}`);
  lines.push(`  Participant scale         : ${provider.participantScale}`);
  lines.push(`  Preliminary category      : ${provider.preliminaryCategory}`);
  lines.push(`  Manual review status      : ${provider.manualReviewStatus}`);
  lines.push("");
  if (result.classifier) {
    lines.push("02 / CLASSIFIER OUTPUT");
    lines.push(classifierToPlainText(result.classifier));
    lines.push("");
  }
  if (result.sourceVerification) {
    lines.push("03 / SOURCE VERIFICATION");
    lines.push(sourceVerificationToPlainText(result.sourceVerification));
    lines.push("");
  }
  lines.push("04 / EXECUTIVE SUMMARY");
  lines.push("-------------------------------------------------------------------------");
  exec.split("\n\n").forEach((p) => {
    lines.push(p);
    lines.push("");
  });
  lines.push("05 / R.E.A.D.Y. SUMMARY");
  lines.push("-------------------------------------------------------------------------");
  result.domainRollup.forEach((d) => {
    lines.push(
      `  ${d.key} \u2014 ${d.title.padEnd(28)} ${RAG_LABELS[d.rag] || "UNKNOWN"}` +
        ` (green ${d.counts.green || 0}, amber ${d.counts.amber || 0}, red ${d.counts.red || 0}, n/a ${d.counts.na || 0})`
    );
  });
  lines.push("");
  lines.push("06 / EVIDENCE READINESS TABLE");
  lines.push("-------------------------------------------------------------------------");
  result.evidenceItems.forEach((i) => {
    lines.push(
      `  ${String(i.number).padStart(2, "0")}.[${i.domain}] ${i.title.padEnd(40)} ${i.shortLabel.padEnd(12)} [${RAG_LABELS[i.rag]}]`
    );
    if (i.canonicalState) lines.push(`        canonical state: ${i.canonicalState}`);
    lines.push(`        finding: ${professionalFinding(i)}`);
    if (i.fieldVerificationLabel) lines.push(`        field verification: ${i.fieldVerificationLabel}`);
  });
  lines.push("");
  lines.push("07 / MISSING EVIDENCE");
  lines.push("-------------------------------------------------------------------------");
  if (result.missingEvidence.length === 0) lines.push("  None recorded.");
  else result.missingEvidence.forEach((i) => lines.push(`  \u2022 [${i.domain}] ${i.title} \u2014 ${evidenceShortNote(i)}`));
  lines.push("");
  lines.push("08 / EVIDENCE NEEDING REVIEW");
  lines.push("-------------------------------------------------------------------------");
  if (result.reviewEvidence.length === 0) lines.push("  None recorded.");
  else result.reviewEvidence.forEach((i) => lines.push(`  \u2022 [${i.domain}] ${i.title} \u2014 ${evidenceShortNote(i)}`));
  lines.push("");
  lines.push("09 / OPERATIONAL RISK SUMMARY");
  lines.push("-------------------------------------------------------------------------");
  riskAreas.forEach((area) =>
    lines.push(`  \u2022 ${area.title.padEnd(34)} [${levelLabel(area.level).toUpperCase()}] \u2014 ${area.label}`)
  );
  lines.push("");
  lines.push("10 / 30 / 60 / 90 DAY PLAN");
  lines.push("-------------------------------------------------------------------------");
  lines.push(" Days 0\u201330 \u2014 Stabilise & identify:");
  result.actionPlan.d30.forEach((a) => lines.push(`  \u2022 ${a}`));
  lines.push("");
  lines.push(" Days 31\u201360 \u2014 Repair & prepare:");
  result.actionPlan.d60.forEach((a) => lines.push(`  \u2022 ${a}`));
  lines.push("");
  lines.push(" Days 61\u201390 \u2014 Review & verify:");
  result.actionPlan.d90.forEach((a) => lines.push(`  \u2022 ${a}`));
  lines.push("");
  lines.push("11 / PROFESSIONAL READINESS FINDINGS");
  lines.push("-------------------------------------------------------------------------");
  lines.push("  Deterministic synthesis of the material readiness findings from this");
  lines.push("  review. Raw consultant reasoning, customer comments and evidence notes");
  lines.push("  are deliberately excluded from this client-facing section.");
  lines.push("");
  if (result.manualReview) {
    lines.push("  Automated categorisation is on hold pending manual consultant review.");
    lines.push("  A synthesised readiness finding cannot be issued for this provider");
    lines.push("  until manual review is complete.");
  } else {
    const txtMissing = (result.evidenceItems || []).filter((i) => i.answer === "missing").length;
    const txtReview = (result.evidenceItems || []).filter((i) => i.answer === "review").length;
    const txtClarify = (result.evidenceItems || []).filter((i) => i.answer === "unsure").length;
    lines.push(
      `  Across ${result.evidenceItems.length} evidence items assessed, ${txtMissing} were reported as missing,`
    );
    lines.push(
      `  ${txtReview} require documentation review, and ${txtClarify} require clarification before a`
    );
    lines.push("  readiness judgement can be issued.");
    lines.push("");
    const txtFindingsItems = (result.evidenceItems || []).filter(
      (i) => i.answer === "missing" || i.answer === "review" || i.answer === "unsure"
    );
    if (txtFindingsItems.length === 0) {
      lines.push("  No material readiness findings \u2014 every evidence item was reported as in");
      lines.push("  place. Spot verification is recommended at the next internal audit cycle.");
    } else {
      txtFindingsItems.forEach((i) => {
        lines.push(`  \u2022 [${i.domain}] ${i.title}`);
        lines.push(`      ${professionalFinding(i)}`);
      });
    }
  }
  lines.push("");
  lines.push("12 / CLARIFICATION REQUIRED");
  lines.push("-------------------------------------------------------------------------");
  lines.push("  Items needing more information, evidence, or context before a readiness");
  lines.push("  judgement can be made. Clarification is a readiness signal, not blame.");
  const txtClarification = clarificationRequiredItems(result);
  if (txtClarification.length === 0) lines.push("  None \u2014 no items are currently awaiting clarification.");
  else txtClarification.forEach((i) => {
    lines.push(`  \u2022 [${i.domain}] ${i.title} \u2014 Needs Clarification`);
  });
  lines.push("");
  lines.push("12b / READINESS SIGNALS");
  lines.push("-------------------------------------------------------------------------");
  lines.push("  Readiness / referral signals gathered at intake (QC-04, QC-05, QC-08).");
  lines.push("  Provider response, evidence status, consultant interpretation and");
  lines.push("  readiness finding are held separately. No pass/fail, compliance,");
  lines.push("  certification or registration-guarantee outcome is derived here.");
  const txtSignals = (result && result.readinessSignals) || [];
  if (txtSignals.length === 0) {
    lines.push("  (No readiness signals in scope for this assessment.)");
  } else {
    txtSignals.forEach((s) => {
      lines.push("");
      lines.push(`  \u2022 Signal:                    ${s.label}`);
      lines.push(`    Provider response:         ${s.responseLabel}`);
      lines.push(`    Evidence status:           ${s.evidenceStatus}`);
      lines.push(`    Consultant interpretation: ${s.interpretation}`);
      lines.push(`    Readiness finding:         ${s.readinessFinding}`);
    });
  }
  lines.push("");
  lines.push("13 / READINESS ACTIONS");
  lines.push("-------------------------------------------------------------------------");
  lines.push("  Consultant-recorded improvement and clarification steps. Not audit");
  lines.push("  findings, failures, or compliance declarations.");
  const txtActions = readinessActions || [];
  if (txtActions.length === 0) lines.push("  (No readiness actions recorded.)");
  else txtActions.forEach((a, idx) => lines.push(`  ${String(idx + 1).padStart(2, "0")}. ${a.text}`));
  lines.push("");
  lines.push("14 / LEGAL & DOCUMENTATION REFERRAL TRIGGERS");
  lines.push("-------------------------------------------------------------------------");
  lines.push("  Readiness indicators, evidence gaps and referral triggers synthesised from");
  lines.push("  provider responses. Not a legal opinion, audit determination, or regulator");
  lines.push("  outcome. Raw customer comments and internal consultant notes are excluded.");
  const txtLegalRows = buildLegalReferralRows(answers);
  txtLegalRows.forEach((r) => {
    lines.push("");
    lines.push(`  \u2022 Area:              ${r.area}`);
    lines.push(`    Evidence requested: ${r.evidenceRequested}`);
    lines.push(`    Evidence status:    ${r.evidenceStatus}`);
    lines.push(`    Red flag:           ${r.redFlag}`);
    lines.push(`    Referral trigger:   ${r.referralTrigger}`);
    lines.push(`    Next action:        ${r.suggestedNextAction}`);
  });
  lines.push("");
  lines.push("=========================================================================");
  lines.push("End of report. Held in local browser state only.");
  return lines.join("\n");
}

export function buildPlainTextSummary({ result, answers, assessmentId, generatedAt }) {
  // Slim version used by the Snapshot page's copy button.
  const lines = [];
  lines.push("NDIS 0138 READINESS PROVIDER INTAKE CLASSIFIER \u2014 PRELIMINARY READINESS SNAPSHOT");
  lines.push("--------------------------------------------------------------------");
  lines.push(`Reference: ${assessmentId}`);
  lines.push(`Generated: ${generatedAt}`);
  lines.push("");
  lines.push("DISCLAIMER");
  lines.push(BOUNDARY_STATEMENT);
  lines.push("");
  if (result.manualReview) {
    lines.push("STATUS: MANUAL REVIEW REQUIRED (scoring on hold)");
    lines.push("Reasons:");
    result.manualReviewReasons.forEach((r) => lines.push(`  \u2022 ${r}`));
    lines.push("");
  } else {
    lines.push("STATUS: Preliminary categorisation complete.");
    lines.push("");
  }
  if (result.classifier) {
    lines.push("CLASSIFIER OUTPUT");
    lines.push(classifierToPlainText(result.classifier));
    lines.push("");
  }
  if (result.sourceVerification) {
    lines.push("SOURCE VERIFICATION");
    lines.push(sourceVerificationToPlainText(result.sourceVerification));
    lines.push("");
  }
  lines.push("PRELIMINARY PROVIDER CATEGORY");
  lines.push(`  ${result.category.title}`);
  lines.push(`  ${result.category.summary}`);
  lines.push("");
  lines.push("R.E.A.D.Y. DOMAIN SUMMARY");
  result.domainRollup.forEach((d) => {
    lines.push(
      `  ${d.key} \u2014 ${d.title}: ${RAG_LABELS[d.rag] || "UNKNOWN"}` +
        ` (green ${d.counts.green || 0}, amber ${d.counts.amber || 0}, red ${d.counts.red || 0}, n/a ${d.counts.na || 0})`
    );
  });
  lines.push("");
  lines.push("EVIDENCE READINESS TABLE (17 items)");
  result.domainRollup.forEach((d) => {
    if (d.items.length === 0) return;
    lines.push(` ${d.key} \u2014 ${d.title}`);
    d.items.forEach((i) => {
      lines.push(`  ${String(i.number).padStart(2, "0")}. [${RAG_LABELS[i.rag]}] ${i.title} \u2014 ${i.label}`);
      if (i.fieldVerificationLabel) lines.push(`      field verification: ${i.fieldVerificationLabel}`);
    });
  });
  lines.push("");
  lines.push("MISSING EVIDENCE");
  if (result.missingEvidence.length === 0) lines.push("  None recorded.");
  else result.missingEvidence.forEach((i) => lines.push(`  \u2022 [${i.domain}] ${i.title} \u2014 ${i.label}`));
  lines.push("");
  lines.push("EVIDENCE NEEDING REVIEW");
  if (result.reviewEvidence.length === 0) lines.push("  None recorded.");
  else result.reviewEvidence.forEach((i) => lines.push(`  \u2022 [${i.domain}] ${i.title} \u2014 ${i.label}`));
  lines.push("");
  lines.push("CLARIFICATION REQUIRED");
  const sumClarification = clarificationRequiredItems(result);
  if (sumClarification.length === 0) lines.push("  None \u2014 no items are currently awaiting clarification.");
  else sumClarification.forEach((i) => lines.push(`  \u2022 [${i.domain}] ${i.title} \u2014 Needs Clarification`));
  lines.push("");
  lines.push("OPERATIONAL RISK SUMMARY");
  result.operationalRisks.forEach((r) =>
    lines.push(`  \u2022 [${r.level.toUpperCase()}] ${r.topic} \u2014 ${r.label}`)
  );
  lines.push("");
  lines.push("30 / 60 / 90 DAY ACTION PLAN");
  lines.push(" Days 0\u201330:");
  result.actionPlan.d30.forEach((a) => lines.push(`  \u2022 ${a}`));
  lines.push(" Days 31\u201360:");
  result.actionPlan.d60.forEach((a) => lines.push(`  \u2022 ${a}`));
  lines.push(" Days 61\u201390:");
  result.actionPlan.d90.forEach((a) => lines.push(`  \u2022 ${a}`));
  lines.push("");
  lines.push("END OF SNAPSHOT");
  return lines.join("\n");
}
